/**
 * Get a formatted namespace for debug messages, supporting both static strings and dynamic functions.
 * @param {string|Function} value - A static string or a function returning a string.
 * @param {string} [defaultPrefix='Debugger'] - Fallback namespace if `value` is nullish or throws.
 * @returns {string} The resolved namespace string (e.g. `'Worker'`). Brackets are added by the caller.
 */
const getNamespace = (value, defaultPrefix = 'Debugger') => {
    let prefix = value
    if (typeof value === 'function') {
        try {
            prefix = value()
        } catch (err) {
            return defaultPrefix
        }
    }
    return String(prefix ?? defaultPrefix)
}

/**
 * Numeric priority for each log level (lower = more severe).
 * @type {Object.<string, number>}
 */
const LEVELS = {
    error: 0,
    warn: 1,
    info: 2,
    debug: 3,
}

const TIME_STYLE = 'color: gray;'

const NS_STYLE = 'color: white; font-weight: bold;'

/**
 * CSS styles applied via `%c` for each log level when `useStyle` is enabled.
 * @type {Object.<string, string>}
 */
const STYLES = {
    error: 'color: #ff4d4f; font-weight: bold;',
    warn: 'color: #faad14; font-weight: bold;',
    info: 'color: #1890ff;',
    debug: 'color: #999;',
}

/**
 * Configurable logger that replaces raw `console.*` calls with
 * level-gated, prefixed, and optionally styled output.
 *
 * @class
 * @classdesc Provides structured logging with four severity levels
 * (`error`, `warn`, `info`, `debug`). Disabled in production by default.
 *
 * Supports:
 * - **Level gating**: messages below the configured threshold are silently dropped.
 * - **Styled output**: uses `%c` CSS formatting in browser/worker DevTools.
 * - **Lazy arguments**: function arguments are only evaluated when the message will be logged.
 * - **Prefixing**: each instance carries a bracketed prefix (e.g. `[MockupApp]`).
 *
 * @example
 * const debug = new Debugger({ namespace: 'MockupApp' })
 * debug.debug('render(fitContain=', true, ')')
 *
 * debug.level = 'warn'   // suppress info + debug
 * debug.enabled = false   // suppress all output
 */
export class Debugger {
    /** @type {boolean} */
    #enabled
    /** @type {string} */
    #level

    /**
     * Whether logging is currently enabled.
     * @type {boolean}
     */
    get enabled() {
        return this.#enabled
    }
    set enabled(value) {
        this.#enabled = Boolean(value)
    }

    /**
     * Current log level threshold. Only messages at or below this
     * severity are emitted. One of `'error'`, `'warn'`, `'info'`, `'debug'`.
     * @type {string}
     * @throws {Error} If set to an invalid level string.
     */
    get level() {
        return this.#level
    }
    set level(value) {
        if (typeof value === 'string' && Object.prototype.hasOwnProperty.call(LEVELS, value)) {
            this.#level = value
        } else {
            throw new Error(`Invalid log level: ${value}`)
        }
    }

    /**
     * Creates a new Debugger instance.
     *
     * @param {Object} [options={}]
     * @param {boolean} [options.enabled=isDev] - Enable/disable logging.
     * @param {string} [options.level=isDev ? 'debug' : 'info'] - Initial log level threshold.
     * @param {string|Function} [options.namespace='Debugger'] - Namespace label shown as `[namespace]` in every
     *   log line. Accepts a plain string or a zero-argument function that returns a string (evaluated once at
     *   construction time).
     * @param {boolean} [options.useStyle=true] - Use `%c` CSS styling in console output.
     */
    constructor(options = {}) {
        const isDev = process.env.NODE_ENV === 'development'
        const {enabled = isDev, level = isDev ? 'debug' : 'info', namespace = 'Debugger', useStyle = true} = options

        this.enabled = enabled
        this.level = level
        /**
         * Resolved namespace string displayed as `[namespace]` in every log line.
         * @type {string}
         */
        this.namespace = getNamespace(namespace)
        /** @type {boolean} */
        this.useStyle = useStyle
    }

    /**
     * Checks whether a message at the given level should be emitted.
     *
     * @param {string} level - One of `'error'`, `'warn'`, `'info'`, `'debug'`.
     * @returns {boolean}
     */
    shouldLog(level) {
        return this.enabled && LEVELS[level] <= LEVELS[this.level]
    }

    getArguments(args) {
        const normalize = (a) => {
            if (typeof a !== 'function') return a
            try {
                return a()
            } catch (e) {
                return `[Debugger.log error: ${e.message}]`
            }
        }
        const [level, ...rest] = args
        if (!Object.prototype.hasOwnProperty.call(LEVELS, level)) {
            return {
                level: 'log',
                finalArgs: args.map(normalize),
            }
        }
        return {
            level,
            finalArgs: rest.map(normalize),
        }
    }

    /**
     * Core logging method. Formats and emits a message if the level passes
     * the threshold check. Function arguments are lazily evaluated.
     *
     * @param {string} level - Log level (`'error'`, `'warn'`, `'info'`, `'debug'`).
     * @param {...*} args - Values to log. Functions are called and their return value used.
     */
    log(...args) {
        const {level, finalArgs} = this.getArguments(args)

        if (!this.shouldLog(level)) return

        const time = new Date().toISOString()
        const logger = console[level].bind(console)
        if (this.useStyle) {
            const styles = [TIME_STYLE, STYLES[level], NS_STYLE]
            logger(
                `%c[${time}] %c[${level.padEnd(5, ' ').toUpperCase()}] %c[${this.namespace}]`,
                ...styles,
                ...finalArgs,
            )
        } else {
            logger(`[${time}] [${level.padEnd(5, ' ').toUpperCase()}] [${this.namespace}]`, ...finalArgs)
        }
    }

    /**
     * Logs an error-level message.
     * @param {...*} args
     */
    error(...args) {
        this.log('error', ...args)
    }

    /**
     * Logs a warn-level message.
     * @param {...*} args
     */
    warn(...args) {
        this.log('warn', ...args)
    }

    /**
     * Logs an info-level message.
     * @param {...*} args
     */
    info(...args) {
        this.log('info', ...args)
    }

    /**
     * Logs a debug-level message.
     * @param {...*} args
     */
    debug(...args) {
        this.log('debug', ...args)
    }
}

const instances = new Map()

export const createDebugger = (options) => {
    const key = getNamespace(options.namespace)
    if (instances.has(key)) {
        return instances.get(key)
    }
    const instance = new Debugger(options)
    instances.set(key, instance)
    return instance
}
